import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FilmStripLoader from '../components/FilmStripLoader';

import trial from "../../src/assets/images/trial-bg.png"
import { FRENCH_GENRES, normalizeGenreFromQuery } from '../constants/genres';

const Movies = () => {
  const [searchParams] = useSearchParams();

  const [movies, setMovies] = useState([]);
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLoader, setShowLoader] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // Pagination state
  const [moviesPage, setMoviesPage] = useState(1);
  const [seriesPage, setSeriesPage] = useState(1);
  const [totalMoviesPages, setTotalMoviesPages] = useState(1);
  const [totalSeriesPages, setTotalSeriesPages] = useState(1);
  const [totalMoviesCount, setTotalMoviesCount] = useState(0);
  const [totalSeriesCount, setTotalSeriesCount] = useState(0);

  // Offset state for mixed view stability
  const [moviesSkip, setMoviesSkip] = useState(0);
  const [seriesSkip, setSeriesSkip] = useState(0);

  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [selectedGenre, setSelectedGenre] = useState(() =>
    normalizeGenreFromQuery(searchParams.get('genre') || '')
  );
  const [contentType, setContentType] = useState('all'); // 'all', 'movies', 'series'

  useEffect(() => {
    setSelectedGenre(normalizeGenreFromQuery(searchParams.get('genre') || ''));
  }, [searchParams]);

  const ITEMS_PER_PAGE = 10;
  const MIXED_ITEMS_PER_PAGE = 5;

  const fetchMoviesPaginated = async (page, search = '', genre = '', skip = null, limit = ITEMS_PER_PAGE) => {
    try {
      setLoadingMore(true);
      const params = {
        limit,
        search,
        genre
      };
      if (skip !== null) {
        params.skip = skip;
      } else {
        params.page = page;
      }

      const res = await axios.get(`/api/movies/paginated`, { params });
      return res.data;
    } catch (error) {
      console.error('Error fetching movies:', error);
      return { movies: [], pagination: { totalPages: 0, hasMore: false, totalItems: 0 } };
    } finally {
      setLoadingMore(false);
    }
  };

  const fetchSeriesPaginated = async (page, search = '', genre = '', skip = null, limit = ITEMS_PER_PAGE) => {
    try {
      setLoadingMore(true);
      const params = {
        limit,
        search,
        genre
      };
      if (skip !== null) {
        params.skip = skip;
      } else {
        params.page = page;
      }

      const res = await axios.get(`/api/series/paginated`, { params });
      return res.data;
    } catch (error) {
      console.error('Error fetching series:', error);
      return { series: [], pagination: { totalPages: 0, hasMore: false, totalItems: 0 } };
    } finally {
      setLoadingMore(false);
    }
  };

  const loadMixedContent = async (mSkip, sSkip) => {
    // 1. Initial Fetch (5 of each)
    const [moviesData, seriesData] = await Promise.all([
      fetchMoviesPaginated(1, searchTerm, selectedGenre, mSkip, MIXED_ITEMS_PER_PAGE),
      fetchSeriesPaginated(1, searchTerm, selectedGenre, sSkip, MIXED_ITEMS_PER_PAGE)
    ]);

    let finalMovies = moviesData.movies;
    let finalSeries = seriesData.series;
    
    // Update Counts immediately to know if we can fetch more
    setTotalMoviesCount(moviesData.pagination.totalItems);
    setTotalSeriesCount(seriesData.pagination.totalItems);
    setTotalMoviesPages(moviesData.pagination.totalPages);
    setTotalSeriesPages(seriesData.pagination.totalPages);

    // 2. Check for deficiency
    const currentCount = finalMovies.length + finalSeries.length;
    let missing = ITEMS_PER_PAGE - currentCount;

    if (missing > 0) {
      // Try to fill from Movies if Series exhausted (or just because we prefer movies? No, standard logic)
      // Actually we should check who has more left.
      // But we just fetched.
      // If moviesData.hasMore is true, we can fetch more movies.
      
      // Strategies: 
      // A. If Series gave < 5, try to get more Movies.
      // B. If Movies gave < 5, try to get more Series.
      
      const moviesDeficiency = MIXED_ITEMS_PER_PAGE - finalMovies.length; // e.g. 5 - 2 = 3
      const seriesDeficiency = MIXED_ITEMS_PER_PAGE - finalSeries.length; // e.g. 5 - 5 = 0

      // If we need more items
      if (seriesDeficiency > 0 && moviesData.pagination.hasMore) {
        // We need more items, and Movies has more.
        // Fetch extra movies.
        // How many? 'missing' amount. 
        // We typically simply fetch more from the one that ISN'T exhausted.
        
        // We already fetched with skip = mSkip.
        // We want to fetch NEXT batch of movies? No, we want to fetch MORE for THIS page.
        // So offset = mSkip + finalMovies.length (which is current fetched).
        const extraMoviesRes = await fetchMoviesPaginated(1, searchTerm, selectedGenre, mSkip + finalMovies.length, seriesDeficiency);
        finalMovies = [...finalMovies, ...extraMoviesRes.movies];
      }
      
      if (moviesDeficiency > 0 && seriesData.pagination.hasMore) {
        // We need more items, and Series has more.
        const extraSeriesRes = await fetchSeriesPaginated(1, searchTerm, selectedGenre, sSkip + finalSeries.length, moviesDeficiency);
        finalSeries = [...finalSeries, ...extraSeriesRes.series];
      }
    }

    setMovies(finalMovies);
    setSeries(finalSeries);
  };

  const loadSingleContent = async (page) => {
    if (contentType === 'movies') {
        const data = await fetchMoviesPaginated(page, searchTerm, selectedGenre, null, ITEMS_PER_PAGE);
        setMovies(data.movies);
        setTotalMoviesCount(data.pagination.totalItems);
        setTotalMoviesPages(data.pagination.totalPages);
    } else if (contentType === 'series') {
        const data = await fetchSeriesPaginated(page, searchTerm, selectedGenre, null, ITEMS_PER_PAGE);
        setSeries(data.series);
        setTotalSeriesCount(data.pagination.totalItems);
        setTotalSeriesPages(data.pagination.totalPages);
    }
  };

  // Debounced search and filter effect
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      // Reset pages/skips when filters change
      setMoviesPage(1);
      setSeriesPage(1);
      setMoviesSkip(0);
      setSeriesSkip(0);

      try {
        if (contentType === 'all') {
          await loadMixedContent(0, 0);
        } else {
          await loadSingleContent(1);
        }
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setTimeout(() => {
          setLoading(false);
          setShowLoader(false);
        }, 500);
      }
    };

    const timer = setTimeout(() => {
      loadData();
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, selectedGenre, searchParams, contentType]);

  const handlePageChange = async (newPage) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });

    const maxPage =
      contentType === 'series'
        ? totalSeriesPages
        : contentType === 'movies'
          ? totalMoviesPages
          : Math.max(totalMoviesPages, totalSeriesPages, 1);
    const page = Math.max(1, Math.min(Number(newPage) || 1, maxPage));

    if (contentType === 'all') {
      // Each mixed "page" pulls ~MIXED_ITEMS_PER_PAGE from movies and the same from series (then fills to ~10 items).
      // Approximate skip for direct page N so numbered pagination / last page work.
      const mSkip = (page - 1) * MIXED_ITEMS_PER_PAGE;
      const sSkip = (page - 1) * MIXED_ITEMS_PER_PAGE;
      setMoviesSkip(mSkip);
      setSeriesSkip(sSkip);
      setMoviesPage(page);
      await loadMixedContent(mSkip, sSkip);
    } else {
      await loadSingleContent(page);
      if (contentType === 'movies') setMoviesPage(page);
      if (contentType === 'series') setSeriesPage(page);
    }
  };

  const currentPage = contentType === 'series' ? seriesPage : moviesPage;
  const totalPages = contentType === 'series' ? totalSeriesPages : 
                     contentType === 'movies' ? totalMoviesPages : 
                     Math.max(totalMoviesPages, totalSeriesPages);


  const formatDuration = (val) => {
    if (val == null) return null;

    // nëse është numër ose string numër -> minuta
    const num = typeof val === 'number' ? val : Number(String(val).trim());
    if (!Number.isNaN(num) && Number.isFinite(num)) {
      const h = Math.floor(num / 60);
      const m = Math.round(num % 60);
      if (h <= 0) return `${m}min`;
      return `${h}h ${m}min`;
    }

    // nëse veç vjen si tekst "1h 30min" etj.
    return String(val);
  };


  // Genres (French) — aligned with DB + API filter
  const genres = FRENCH_GENRES;

  // No client-side filtering needed for search/genre anymore
  // But we still toggle visibility based on contentType
  const displayMovies = contentType === 'series' ? [] : movies;
  const displaySeries = contentType === 'movies' ? [] : series;

  if (showLoader) {
    return (
      <div className={loading ? '' : 'fade-out-loader'}>
        <FilmStripLoader />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#141414] flex flex-col">
      <Header />

      <div className="flex-1 container mx-auto px-4 py-8">
        <h1 className="text-4xl font-bold text-white mb-8">Films & Séries</h1>

        {/* Search and Filter */}
        <div className="mb-8 flex flex-col md:flex-row gap-4">
          <input
            type="text"
            placeholder="Rechercher des films et séries..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 px-4 py-3 bg-[#101010] text-white rounded-xl border border-[#262626] focus:border-white/30 focus:outline-none"
          />

          {/* Content type */}
          <div className="relative">
            <select
              value={contentType}
              onChange={(e) => setContentType(e.target.value)}
              className="w-full md:w-[220px] px-4 py-3 pr-12 bg-[#101010] text-white rounded-xl border border-[#262626] focus:border-white/30 focus:outline-none appearance-none"
            >
              <option value="all">Tout le contenu</option>
              <option value="movies">Films uniquement</option>
              <option value="series">Séries uniquement</option>
            </select>

            <svg
              className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-white/70"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M6 9l6 6 6-6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          {/* Genre */}
          <div className="relative">
            <select
              value={selectedGenre}
              onChange={(e) => setSelectedGenre(e.target.value)}
              className="w-full md:w-[220px] px-4 py-3 pr-12 bg-[#101010] text-white rounded-xl border border-[#262626] focus:border-white/30 focus:outline-none appearance-none"
            >
              <option value="">Tous les genres</option>
              {genres.map((genre, index) => (
                <option key={index} value={genre}>
                  {genre}
                </option>
              ))}
            </select>

            <svg
              className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-white/70"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M6 9l6 6 6-6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>


        {/* Content Grid */}
          {displayMovies.length > 0 || displaySeries.length > 0 ? (
            <>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {/* Movies */}
              {displayMovies.map((movie) => (
                <Link
                  key={movie._id}
                  to={`/movie/${movie._id}`}
                  className="group relative rounded-2xl border border-[#262626] bg-[#141414] p-4 hover:bg-[#161616] transition"
                >
                  {/* Poster */}
                  <div
                    className="relative w-full overflow-hidden rounded-2xl bg-[#0F0F0F]"
                    style={{ paddingBottom: '140%' }}
                  >
                    <img
                      src={movie.poster}
                      alt={movie.title}
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-300"
                      onError={(e) => {
                        e.currentTarget.src = 'https://via.placeholder.com/300x450?text=No+Image';
                      }}
                    />
                  </div>
                  <div className="mt-4 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-black/30 border border-[#262626] text-gray-200">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M12 8V12L14.5 13.5"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                          stroke="currentColor"
                          strokeWidth="2"
                        />
                      </svg>
                      <span className="text-sm font-medium">
                        {formatDuration(movie.duration) || '1h 30min'}
                      </span>

                    </div>
                      <svg
                        width="21"
                        height="19"
                        viewBox="0 0 21 19"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M1 9.4375L19.75 9.4375M19.75 9.4375L11.3125 1M19.75 9.4375L11.3125 17.875"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>

                  </div>

                  {/* Title */}
                  <div className="mt-3">
                    <h3 className="text-white font-semibold text-sm line-clamp-1">
                      {movie.title}
                    </h3>
                    <p className="text-gray-400 text-xs mt-1">{movie.releaseYear || ''}</p>
                  </div>
                </Link>
              ))}

              {/* Series */}
              {displaySeries.map((s) => (
                <Link
                  key={s._id}
                  to={`/series/${s._id}`}
                  className="group relative rounded-2xl border border-[#262626] bg-[#141414] p-4 hover:bg-[#161616] transition"
                >
                  <div
                    className="relative w-full overflow-hidden rounded-2xl bg-[#0F0F0F]"
                    style={{ paddingBottom: '140%' }}
                  >
                    <img
                      src={s.poster}
                      alt={s.title}
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-300"
                      onError={(e) => {
                        e.currentTarget.src = 'https://via.placeholder.com/300x450?text=No+Image';
                      }}
                    />
                  </div>

                  <div className="mt-4 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-black/30 border border-[#262626] text-gray-200">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M12 8V12L14.5 13.5"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                          stroke="currentColor"
                          strokeWidth="2"
                        />
                      </svg>
                      <span className="text-sm font-medium">
                        {formatDuration(s.duration) || (s.seasons?.length === 1 ? '1 Season' : `${s.seasons?.length || 0} Seasons`)}
                      </span>

                    </div>
                      <svg
                        width="21"
                        height="19"
                        viewBox="0 0 21 19"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M1 9.4375L19.75 9.4375M19.75 9.4375L11.3125 1M19.75 9.4375L11.3125 17.875"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>

                  </div>

                  <div className="mt-3">
                    <h3 className="text-white font-semibold text-sm line-clamp-1">{s.title}</h3>
                    <p className="text-gray-400 text-xs mt-1">Série TV</p>
                  </div>
                </Link>
              ))}
            </div>

            {/* Pagination UI */}
            {totalPages > 1 && (
              <div className="mt-12 flex flex-col items-center gap-6">
                <div className="flex flex-wrap justify-center items-center gap-2 px-2">
                  <button
                    type="button"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1 || loadingMore}
                    className="p-3 rounded-xl border border-[#262626] bg-[#101010] text-white hover:bg-[#161616] disabled:opacity-30 disabled:cursor-not-allowed transition"
                    title="Previous Page"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                  
                  <div className="flex items-center gap-2">
                    {(() => {
                      const delta = 2;
                      const range = [];
                      const rangeWithDots = [];
                      let l;

                      for (let i = 1; i <= totalPages; i++) {
                        if (i === 1 || i === totalPages || (i >= currentPage - delta && i <= currentPage + delta)) {
                          range.push(i);
                        }
                      }

                      for (let i of range) {
                        if (l) {
                          if (i - l === 2) {
                            rangeWithDots.push(l + 1);
                          } else if (i - l !== 1) {
                            rangeWithDots.push('...');
                          }
                        }
                        rangeWithDots.push(i);
                        l = i;
                      }

                      return rangeWithDots.map((pageNum, index) => {
                        if (pageNum === '...') {
                          return (
                            <span key={`dots-${index}`} className="w-[45px] text-center text-gray-500">
                              ...
                            </span>
                          );
                        }
                        return (
                          <button
                            type="button"
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            disabled={loadingMore}
                            className={`min-w-[45px] h-[45px] rounded-xl border transition font-medium ${
                              currentPage === pageNum
                                ? 'bg-white text-black border-white'
                                : 'bg-[#101010] text-white border-[#262626] hover:bg-[#161616]'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      });
                    })()}
                  </div>

                  <button
                    type="button"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages || loadingMore}
                    className="p-3 rounded-xl border border-[#262626] bg-[#101010] text-white hover:bg-[#161616] disabled:opacity-30 disabled:cursor-not-allowed transition"
                    title="Next Page"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
                <div className="flex items-center gap-3 text-gray-500 text-sm">
                  <span>Page {currentPage} sur {totalPages}</span>
                  {loadingMore && (
                    <div className="flex items-center gap-2 text-white/70">
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      <span>Chargement...</span>
                    </div>
                  )}
                </div>
              </div>
            )}
            </>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-400 text-xl">Aucun contenu trouvé</p>
              {searchTerm && <p className="text-gray-500 mt-2">Essayez un autre terme de recherche</p>}
            </div>
          )}

        {/* Results count */}
        {(totalMoviesCount > 0 || totalSeriesCount > 0) && (
          <div className="mt-8 text-gray-400">
            {contentType === 'all' && `Found ${totalMoviesCount + totalSeriesCount} results (${totalMoviesCount} movies, ${totalSeriesCount} series)`}
            {contentType === 'movies' && `Found ${totalMoviesCount} movies`}
            {contentType === 'series' && `Found ${totalSeriesCount} series`}
          </div>
        )}
      </div>
        <div className="container mx-auto px-4 mb-12">
          <div className="relative rounded-2xl overflow-hidden">
            <img
              src={trial}
              alt="Free trial background"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 " />
            <div className="relative z-10 p-8 md:p-20 ">
                <div className='flex items-center justify-between flex-col md:flex-row'>
                    <div className='flex flex-col items-start'>
                      <h2 className="text-center md:text-start text-3xl md:text-4xl font-semibold text-white mb-4">
                        Des milliers de films et séries, dès maintenant !
                      </h2>

                      <p className="text-gray-300 mb-6 text-center md:text-start">
                        Accès illimité au catalogue complet en Full HD 1080p, sans publicité.<br />
                        Compatible TV, PC, Mobile et Mac.
                      </p>                
                    </div>
                    <Link
                      to="/subscriptions"
                      className="inline-flex items-center justify-center px-6 py-3
                                bg-white text-gray-900 rounded-xl
                                hover:bg-gray-100 transition font-semibold"
                    >
                      Accès immédiat
                    </Link>                  
                </div>
            </div>

          </div>
        </div>      

      <Footer />
    </div>
  );
};

export default Movies;
